import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { createNewChapter } from "../../../apis/CourseApis";
import { useAuth } from "../../Authentication/AuthContext";
import { useToastHelpers } from "../../Toasts/ToastUtils";
import SeoMeta from "../../SeoMeta";

export default function AddChapter() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { successToast, errorToast } = useToastHelpers();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    courseId: courseId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Chapter title is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Chapter description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const chapterData = {
        title: formData.title,
        description: formData.description,
        courseId: courseId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await createNewChapter(chapterData, user?.token, courseId);
      successToast("Chapter created successfully!");
      navigate(`/courses/${courseId}`);
    } catch (err) {
      console.error("Error creating chapter:", err);
      errorToast("Failed to create chapter. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SeoMeta
        title="Add Chapter - Code Arena Admin"
        description="Create a new chapter for a course"
        url={`https://www.code-arena.in/courses/${courseId}/chapters/new`}
        image="/logo192.png"
      />

      <div className="ca-container max-w-3xl py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="flex items-center gap-2 text-accent-600 hover:text-accent-700 font-medium mb-6 transition-colors"
        >
          <FaArrowLeft /> Back to Course
        </button>

        {/* Form Card */}
        <div className="ca-card overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-accent-600 via-royal-600 to-emerald-600 px-4 sm:px-8 py-5 sm:py-6">
            <h1 className="text-3xl font-bold text-white">Create New Chapter</h1>
            <p className="text-white/80 mt-2">Add a new chapter to this course</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
            {/* Chapter Title */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
                Chapter Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Getting Started with React"
                className={`ca-input py-3 ${
                  errors.title
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Chapter Description */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
                Chapter Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what this chapter covers..."
                rows="5"
                className={`ca-input py-3 ${
                  errors.description
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.description && (
                <p className="text-red-600 text-sm mt-1">{errors.description}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-neutral-200 dark:border-neutral-700">
              <button
                type="button"
                onClick={() => navigate(`/courses/${courseId}`)}
                className="ca-btn-secondary flex-1 px-6 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ca-btn-primary flex-1 px-6 py-3"
              >
                {isSubmitting ? "Creating..." : "Create Chapter"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
